import React from 'react';
import Slider from 'components/commercetools-ui/atoms/slider';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile, tablet, mediumDesktop, desktop, largeDesktop, hugeDesktop } from 'helpers/utils/screensizes';
import { breakpoints } from 'helpers/utils/breakpoints';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';

// Nova ← Follow Us section: grey background, serif heading, social icons top-right,
// multi-image carousel with title + description below each card.
//
// Unlike every other rendering this one carries NO section margin (my-0, at every width) instead of
// SECTION_SPACING. Its own padding sits inside the grey block, so the shared rhythm would have added
// a second, outer band of space around a section that already looks spaced.
export default function NovaRendering({ content }: { content: GenericSectionContent }) {
  const { title, cards = [], ctas = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'NovaRendering');
  const [isTabletSize] = useMediaQuery(tablet);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`my-0 bg-[#EDEDED] ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div className="pt-32 pb-40 pl-24 md:pb-70 md:pl-28 lg:pl-48 xl:pb-100 xl:pl-60 xl:pt-40 2xl:pb-120 2xl:pl-80 2xl:pt-60">
          <div className="mb-16 md:mb-24 2xl:mb-32 flex items-center justify-between">
            {title && (
              <Typography as="h3" className={`text-32 font-medium text-[#090909] xl:text-36 2xl:text-[3rem] ${Tiempos.className}`}>
                {title}
              </Typography>
            )}
            {ctas.length > 0 && (
              <div className="pt-10 mr-20 flex flex-row gap-12 self-center 2xl:gap-20">
                {ctas.map((cta, i) => (
                  <Link key={i} link={cta?.url ?? '#'} target={cta?.target || '_blank'} rel="noopener noreferrer">
                    {cta?.icon?.url && (
                      <Image src={cta.icon.url} alt={cta?.icon?.description ?? 'social-icon'} height={24} width={24} className="mb-20 h-auto w-20 2xl:w-24" />
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="px-4 py-6">
            <Slider
              dots={false}
              allowTouchMove
              arrows={isTabletSize}
              innerArrows
              allowArrowsOnTouchDevice
              solidArrows
              nextButtonStyles={{ top: '120%', left: 50 }}
              prevButtonStyles={{ top: '120%', left: 0 }}
              slidesPerView={1.5}
              spaceBetween={16}
              breakpoints={{
                [mobile]: { slidesPerView: 2.75, spaceBetween: 16, ...(breakpoints[mobile] ?? {}) },
                [tablet]: { slidesPerView: 2.7, spaceBetween: 16, ...(breakpoints[tablet] ?? {}) },
                [mediumDesktop]: { slidesPerView: 5.85, spaceBetween: 16, ...(breakpoints[mediumDesktop] ?? {}) },
                [desktop]: { slidesPerView: 3.75, spaceBetween: 16, ...(breakpoints[desktop] ?? {}) },
                [largeDesktop]: { slidesPerView: 5.5, spaceBetween: 16, ...(breakpoints[largeDesktop] ?? {}) },
                [hugeDesktop]: { slidesPerView: 6.5, spaceBetween: 16, ...(breakpoints[hugeDesktop] ?? {}) },
              }}
            >
              {cards.map((card, index) => (
                <a key={index} href={card?.external_media_url ?? card?.ctas?.[0]?.url ?? '#'}>
                  <div className="hover:bg-[#D9D9D9]">
                    {card?.desktop_image?.url && (
                      <Image
                        loading="lazy"
                        src={card.desktop_image.url}
                        alt={card.desktop_image.description ?? ''}
                        width={220}
                        height={220}
                        className="h-auto w-full object-cover"
                      />
                    )}
                    {card?.title && (
                      <p className={`line-clamp-2 text-16 font-semibold capitalize leading-[1.625rem] text-[#2B2C39] md:text-14 lg:text-primary-black 2xl:text-16 ${NationalBook.className}`}>
                        {card.title}
                      </p>
                    )}
                    {card?.description && (
                      <p className={`text-14 font-normal leading-6 text-[#000000] md:text-12 2xl:text-14 ${NationalBook.className}`}>
                        {card.description}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </Slider>
          </div>
        </div>
      </div>
    </>
  );
}
